import * as THREE from 'three/webgpu';
import {computeDirectionArrowWorldSizeFromPixels} from '../../manager/directionArrow';
import type {
    SketchCircularPatternPreviewGuideFrame,
} from '../../../types/sketch/circular-pattern/SketchCircularPatternPreviewGuide';

/** 辅助圆完整一周的采样段数。 */
const FULL_CIRCLE_SEGMENT_COUNT = 128;

/** 部分扫掠圆弧的最小采样段数。 */
const MINIMUM_ARC_SEGMENT_COUNT = 8;

/** 有效几何计算容差。 */
const GUIDE_GEOMETRY_EPSILON = 1e-10;

/** 圆周阵列命令态辅助线颜色。 */
const GUIDE_COLOR = 0x246bff;

/** 方向箭头目标屏幕长度。 */
const DIRECTION_ARROW_PIXEL_LENGTH = 64;

/**
 * 圆周草图阵列预览辅助对象。
 *
 * 职责：
 * 1. 依据阵列中心、半径、扫掠角和方向绘制暂态辅助圆弧；
 * 2. 绘制从阵列中心指向种子锚点的半径线；
 * 3. 在第一个阵列间隔位置绘制固定屏幕尺寸的切向方向箭头；
 * 4. 统一回收自身创建的几何体与材质。
 *
 * 该对象仅属于命令态 Viewer 覆盖层，不进入草图组、约束、持久化或撤销重做历史。
 */
export class SketchCircularPatternPreviewGuide extends THREE.Group {
    /**
     * 纯视觉辅助线的空拾取实现。
     *
     * 显式覆写后，统一场景入口会保留该行为并跳过 BVH 构建，避免不可选择的命令预览浪费加速资源。
     */
    private static readonly DISABLED_RAYCAST: THREE.Object3D['raycast'] = (): void => undefined;

    /** 运行时类型标记，供场景过滤与诊断使用。 */
    public readonly isSketchCircularPatternPreviewGuide = true;

    /** 跳过普通实体选择链。 */
    public readonly skipSelection = true;

    /** 当前辅助线使用的共享材质。 */
    private readonly guideMaterial: THREE.LineBasicMaterial;

    /** 当前阵列实际扫掠圆弧。 */
    private readonly sweepGuide: THREE.Line;

    /** 阵列中心到种子锚点的径向线。 */
    private readonly radiusGuide: THREE.Line;

    /** 当前圆周传播方向箭头。 */
    private readonly directionArrow: THREE.LineSegments;

    /** 当前阵列中心世界坐标。 */
    private readonly centerWorld = new THREE.Vector3();

    /** 当前草图参考面 X 轴世界方向。 */
    private readonly xAxisWorld = new THREE.Vector3(1, 0, 0);

    /** 当前草图参考面 Y 轴世界方向。 */
    private readonly yAxisWorld = new THREE.Vector3(0, 1, 0);

    /** 当前箭头所在圆周位置。 */
    private readonly arrowAnchorWorld = new THREE.Vector3();

    /** 当前箭头切向方向。 */
    private readonly arrowDirectionWorld = new THREE.Vector3(0, 1, 0);

    /** 当前箭头相对圆心的径向方向。 */
    private readonly arrowRadialWorld = new THREE.Vector3(1, 0, 0);

    /** 几何计算复用世界坐标。 */
    private readonly pointWorld = new THREE.Vector3();

    /** 箭头几何计算复用世界坐标。 */
    private readonly arrowPointWorld = new THREE.Vector3();

    /** 当前阵列半径。 */
    private radius = 0;

    /** 当前种子定位角。 */
    private startAngleRad = 0;

    /** 当前带方向符号的扫掠角。 */
    private signedSweepAngleRad = 0;

    /** 当前方向箭头相对种子的无符号角间距。 */
    private directionArrowOffsetAngleRad = 0;

    /** 读取当前相机的回调。 */
    private cameraProvider: (() => THREE.Camera | null | undefined) | null = null;

    /** 读取当前视口容器的回调。 */
    private containerProvider: (() => HTMLElement | null | undefined) | null = null;

    /**
     * 创建圆周阵列预览辅助对象。
     */
    public constructor() {
        super();
        this.name = '圆周草图阵列-预览辅助对象';
        this.renderOrder = 92;
        this.userData.skipSelection = true;
        this.userData.skipSelectionSubtree = true;

        this.guideMaterial = new THREE.LineBasicMaterial({
            color: GUIDE_COLOR,
            depthTest: false,
            depthWrite: false,
            transparent: true,
            opacity: 0.95,
        });
        this.sweepGuide = this.createSweepGuide();
        this.radiusGuide = this.createRadiusGuide();
        this.directionArrow = this.createDirectionArrow();
        this.add(this.sweepGuide, this.radiusGuide, this.directionArrow);
        this.installScreenSizeSyncHook();
    }

    /**
     * 绑定方向箭头固定屏幕尺寸所需的视口上下文。
     *
     * @param cameraProvider 当前相机读取回调。
     * @param containerProvider 当前视口容器读取回调。
     */
    public bindScreenSizeContext(
        cameraProvider: () => THREE.Camera | null | undefined,
        containerProvider: () => HTMLElement | null | undefined,
    ): void {
        this.cameraProvider = cameraProvider;
        this.containerProvider = containerProvider;
        this.syncDirectionArrowScreenSize();
    }

    /**
     * 更新圆周阵列辅助对象的完整几何帧。
     *
     * @param frame 阵列中心、参考面和扫掠参数。
     */
    public updateFrame(frame: SketchCircularPatternPreviewGuideFrame): void {
        const normalizedRadius = Number.isFinite(frame.radius) ? Math.max(0, frame.radius) : 0;
        const normalizedSweep = Number.isFinite(frame.totalAngleRad)
            ? THREE.MathUtils.clamp(Math.abs(frame.totalAngleRad), 0, Math.PI * 2)
            : 0;
        const directionSign = frame.direction === 'clockwise' ? -1 : 1;
        const normalizedArrowOffset = Number.isFinite(frame.directionArrowOffsetAngleRad)
            ? THREE.MathUtils.clamp(Math.abs(frame.directionArrowOffsetAngleRad), 0, normalizedSweep)
            : 0;

        this.centerWorld.copy(frame.centerWorld);
        this.xAxisWorld.copy(frame.xAxisWorld).normalize();
        this.yAxisWorld.copy(frame.yAxisWorld).normalize();
        this.radius = normalizedRadius;
        this.startAngleRad = Number.isFinite(frame.startAngleRad) ? frame.startAngleRad : 0;
        this.signedSweepAngleRad = normalizedSweep * directionSign;
        this.directionArrowOffsetAngleRad = normalizedArrowOffset;
        this.visible = normalizedRadius > GUIDE_GEOMETRY_EPSILON
            && normalizedSweep > GUIDE_GEOMETRY_EPSILON
            && this.xAxisWorld.lengthSq() > GUIDE_GEOMETRY_EPSILON
            && this.yAxisWorld.lengthSq() > GUIDE_GEOMETRY_EPSILON;

        if (!this.visible) {
            return;
        }

        // 三类辅助视觉共享同一帧参数，必须一次更新，避免面板快速输入时出现方向与圆弧不同步。
        this.updateSweepGuideGeometry();
        this.updateRadiusGuideGeometry();
        this.updateDirectionFrame();
        this.syncDirectionArrowScreenSize();
    }

    /**
     * 释放辅助对象持有的全部 GPU 资源。
     */
    public dispose(): void {
        this.sweepGuide.geometry.dispose();
        this.radiusGuide.geometry.dispose();
        this.directionArrow.geometry.dispose();
        this.guideMaterial.dispose();
        this.cameraProvider = null;
        this.containerProvider = null;
    }

    /**
     * 创建扫掠圆弧线。
     *
     * @returns 尚未分配顶点缓冲的圆弧线；首个有效参数帧到达后再创建精确缓冲。
     */
    private createSweepGuide(): THREE.Line {
        const geometry = new THREE.BufferGeometry();
        const line = new THREE.Line(geometry, this.guideMaterial);
        line.name = '圆周草图阵列-扫掠辅助线';
        line.renderOrder = 92;
        line.frustumCulled = false;
        line.raycast = SketchCircularPatternPreviewGuide.DISABLED_RAYCAST;
        return line;
    }

    /**
     * 创建中心到种子锚点的径向辅助线。
     *
     * @returns 径向辅助线。
     */
    private createRadiusGuide(): THREE.Line {
        const geometry = new THREE.BufferGeometry();
        const line = new THREE.Line(geometry, this.guideMaterial);
        line.name = '圆周草图阵列-径向辅助线';
        line.renderOrder = 92;
        line.frustumCulled = false;
        line.raycast = SketchCircularPatternPreviewGuide.DISABLED_RAYCAST;
        return line;
    }

    /**
     * 创建平面内切向方向箭头。
     *
     * 箭头由一段杆身和两段箭头翼组成，保持 SolidWorks 草图命令中的线框式方向提示，
     * 同时避免把三维实体箭头混入二维草图视觉。
     *
     * @returns 方向箭头线段集合。
     */
    private createDirectionArrow(): THREE.LineSegments {
        const geometry = new THREE.BufferGeometry();
        const arrow = new THREE.LineSegments(geometry, this.guideMaterial);
        arrow.name = '圆周草图阵列-方向箭头';
        arrow.renderOrder = 93;
        arrow.frustumCulled = false;
        arrow.raycast = SketchCircularPatternPreviewGuide.DISABLED_RAYCAST;
        return arrow;
    }

    /**
     * 按当前实际扫掠范围更新辅助圆或圆弧。
     */
    private updateSweepGuideGeometry(): void {
        const sweepMagnitude = Math.abs(this.signedSweepAngleRad);
        const segmentCount = Math.min(
            FULL_CIRCLE_SEGMENT_COUNT,
            Math.max(
                MINIMUM_ARC_SEGMENT_COUNT,
                Math.ceil(FULL_CIRCLE_SEGMENT_COUNT * sweepMagnitude / (Math.PI * 2)),
            ),
        );
        const position = this.ensureExactPositionBuffer(this.sweepGuide, segmentCount + 1);

        for (let index = 0; index <= segmentCount; index += 1) {
            const ratio = index / segmentCount;
            const angle = this.startAngleRad + this.signedSweepAngleRad * ratio;
            this.resolveCircleWorldPoint(angle, this.pointWorld);
            position.setXYZ(index, this.pointWorld.x, this.pointWorld.y, this.pointWorld.z);
        }
        position.needsUpdate = true;
    }

    /**
     * 更新阵列中心到种子锚点的径向线。
     */
    private updateRadiusGuideGeometry(): void {
        const position = this.ensureExactPositionBuffer(this.radiusGuide, 2);
        this.resolveCircleWorldPoint(this.startAngleRad, this.pointWorld);
        position.setXYZ(0, this.centerWorld.x, this.centerWorld.y, this.centerWorld.z);
        position.setXYZ(1, this.pointWorld.x, this.pointWorld.y, this.pointWorld.z);
        position.needsUpdate = true;
    }

    /**
     * 更新箭头锚点、切向方向和径向方向。
     */
    private updateDirectionFrame(): void {
        const directionSign = Math.sign(this.signedSweepAngleRad) || 1;
        const arrowAngle = this.startAngleRad + this.directionArrowOffsetAngleRad * directionSign;
        const cosine = Math.cos(arrowAngle);
        const sine = Math.sin(arrowAngle);

        this.resolveCircleWorldPoint(arrowAngle, this.arrowAnchorWorld);
        this.arrowRadialWorld.copy(this.xAxisWorld).multiplyScalar(cosine)
            .addScaledVector(this.yAxisWorld, sine)
            .normalize();
        this.arrowDirectionWorld.copy(this.xAxisWorld).multiplyScalar(-sine * directionSign)
            .addScaledVector(this.yAxisWorld, cosine * directionSign)
            .normalize();
    }

    /**
     * 把指定局部极角映射为当前参考面上的世界坐标。
     *
     * @param angleRad 局部极角。
     * @param out 输出世界坐标。
     * @returns 输出世界坐标。
     */
    private resolveCircleWorldPoint(angleRad: number, out: THREE.Vector3): THREE.Vector3 {
        return out.copy(this.centerWorld)
            .addScaledVector(this.xAxisWorld, Math.cos(angleRad) * this.radius)
            .addScaledVector(this.yAxisWorld, Math.sin(angleRad) * this.radius);
    }

    /**
     * 根据当前相机与视口同步方向箭头世界尺寸。
     */
    private syncDirectionArrowScreenSize(): void {
        if (!this.visible || this.radius <= GUIDE_GEOMETRY_EPSILON) {
            return;
        }
        const arrowLength = computeDirectionArrowWorldSizeFromPixels(
            this.cameraProvider?.(),
            this.containerProvider?.(),
            this.arrowAnchorWorld,
            DIRECTION_ARROW_PIXEL_LENGTH,
        );
        const headLength = arrowLength * 0.36;
        const headHalfWidth = arrowLength * 0.22;
        const position = this.ensureExactPositionBuffer(this.directionArrow, 6);

        // 圆周锚点就是箭头尖端，整根杆身只沿传播反方向延伸，禁止让圆周点落在箭头中部。
        this.arrowPointWorld.copy(this.arrowAnchorWorld)
            .addScaledVector(this.arrowDirectionWorld, -arrowLength);
        position.setXYZ(0, this.arrowPointWorld.x, this.arrowPointWorld.y, this.arrowPointWorld.z);
        position.setXYZ(1, this.arrowAnchorWorld.x, this.arrowAnchorWorld.y, this.arrowAnchorWorld.z);

        // 两个箭头翼与杆身共享圆周尖端，尾端沿切线回退并向径向两侧展开。
        position.setXYZ(2, this.arrowAnchorWorld.x, this.arrowAnchorWorld.y, this.arrowAnchorWorld.z);
        this.arrowPointWorld.copy(this.arrowAnchorWorld)
            .addScaledVector(this.arrowDirectionWorld, -headLength)
            .addScaledVector(this.arrowRadialWorld, headHalfWidth);
        position.setXYZ(3, this.arrowPointWorld.x, this.arrowPointWorld.y, this.arrowPointWorld.z);
        position.setXYZ(4, this.arrowAnchorWorld.x, this.arrowAnchorWorld.y, this.arrowAnchorWorld.z);
        this.arrowPointWorld.copy(this.arrowAnchorWorld)
            .addScaledVector(this.arrowDirectionWorld, -headLength)
            .addScaledVector(this.arrowRadialWorld, -headHalfWidth);
        position.setXYZ(5, this.arrowPointWorld.x, this.arrowPointWorld.y, this.arrowPointWorld.z);
        position.needsUpdate = true;
    }

    /**
     * 确保线对象拥有与当前实际顶点数完全一致的位置缓冲。
     *
     * 只有顶点数量变化时才替换几何体；角度、中心或半径变化但采样数不变时继续原位更新。
     * 替换前释放旧几何体，使旧 GPU 缓冲与可能存在的加速结构都有明确回收路径。
     *
     * @param line 目标辅助线对象。
     * @param vertexCount 当前实际需要的顶点数量。
     * @returns 可直接写入的位置属性。
     */
    private ensureExactPositionBuffer(
        line: THREE.Line | THREE.LineSegments,
        vertexCount: number,
    ): THREE.BufferAttribute {
        const currentPosition = line.geometry.getAttribute('position');
        if (currentPosition instanceof THREE.BufferAttribute && currentPosition.count === vertexCount) {
            return currentPosition;
        }

        const previousGeometry = line.geometry;
        const nextGeometry = new THREE.BufferGeometry();
        const nextPosition = new THREE.BufferAttribute(new Float32Array(vertexCount * 3), 3);
        nextGeometry.setAttribute('position', nextPosition);
        line.geometry = nextGeometry;
        previousGeometry.dispose();
        return nextPosition;
    }

    /**
     * 安装渲染前箭头屏幕尺寸同步钩子。
     */
    private installScreenSizeSyncHook(): void {
        this.sweepGuide.onBeforeRender = (): void => {
            this.syncDirectionArrowScreenSize();
        };
    }
}
